import {
  addToast,
  Button,
  CircularSpinnerß,
  ErrorToast,
  Inputß,
} from '@otter-ai/den-components';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { descriptionFormatRules } from '@/features/account-settings/shared/utils/text';
import { useVerifyTwoFactorCode } from '@/query/mutations/useVerifyTwoFactorCodeMutation';
import { FormattedText } from '@/shared/components/FormattedText';
import type { Security2FATypeEnabled } from '@/shared/types/auth';
import { dataTestIds } from '../dataTestIds';

/**
 * The form for verifying a two-factor code during setup.
 */
export function VerifyCodeForm({
  twoFactorAuthType,
  onFinishSetup,
  onPickDifferentMethod,
}: {
  twoFactorAuthType: Security2FATypeEnabled;
  onFinishSetup: () => void;
  onPickDifferentMethod?: () => void;
}) {
  const t = useTranslations('settings:security.verifyCodeForm');

  const { verifyCode } = useVerifyTwoFactorCode();
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isDelayedVerification, setIsDelayedVerification] = useState(false);

  const onSubmitVerifyCode = async (data: { code: string }) => {
    if (isVerifyingCode) {
      return;
    }

    setIsVerifyingCode(true);
    const verifiedResult = await verifyCode({
      code: data.code,
      twoFactorType: twoFactorAuthType,
    });
    setIsVerifyingCode(false);
    if (verifiedResult.isVerified) {
      onFinishSetup();
    } else {
      addToast(<ErrorToast description={t('invalidVerificationCodeToast')} />);
      setIsDelayedVerification(true);
      setTimeout(() => {
        setIsDelayedVerification(false);
      }, verifiedResult.delayBeforeRetryingMs);
    }
  };

  const {
    handleSubmit,
    register,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      code: '',
    },
    mode: 'onChange',
  });

  return (
    <div className="flex flex-col gap-4">
      <FormattedText>
        {t.rich('enterVerificationCodeStep', descriptionFormatRules({}))}
      </FormattedText>
      <div>
        {isDelayedVerification && <div>{t('pleaseWaitBeforeTryingAgain')}</div>}
        <form
          onSubmit={handleSubmit(onSubmitVerifyCode)}
          className="flex flex-col gap-4 max-w-md"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-2">
            <Inputß
              as="text"
              inputSize="lg"
              placeholder={t('enterVerificationCodePlaceholder')}
              inputMode="numeric"
              className="w-full sm:w-auto sm:min-w-[12em]"
              {...register('code', {
                required: t('codeRequired'),
                pattern: {
                  value: /^\d{6}$/,
                  message: t('codeMustBe6Digits'),
                },
                setValueAs: (value) => sanitizeCodeInput(value),
                onChange: (e) => {
                  e.stopPropagation();
                  e.target.value = sanitizeCodeInput(e.target.value);
                },
                disabled: isVerifyingCode || isDelayedVerification,
              })}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              data-testid="2fa-setup-verify-code-input-field"
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={isVerifyingCode || !isValid || isDelayedVerification}
                className="flex-1 sm:flex-none"
                size="lg"
                data-testid={dataTestIds.verifyTotpButton}
              >
                {isVerifyingCode ? (
                  <CircularSpinnerß
                    size="xsm"
                    data-testid="verify-code-loading"
                  />
                ) : (
                  t('verifyCodeButton')
                )}
              </Button>
              {onPickDifferentMethod ? (
                <Button
                  variant="outlineNeutral"
                  version="legacy"
                  className="flex-1 sm:flex-none"
                  size="lg"
                  onClick={onPickDifferentMethod}
                  data-testid={dataTestIds.cancelTotpButton}
                >
                  {t('cancelButton')}
                </Button>
              ) : null}
            </div>
          </div>
          <div>
            <FormattedText className="text-sm max-w-[500px]">
              {t.rich('turningOnWillSignYouOut', descriptionFormatRules({}))}
            </FormattedText>
          </div>
        </form>
      </div>
    </div>
  );
}

function sanitizeCodeInput(value: string, length = 6) {
  return value.replace(/\D/g, '').substring(0, length);
}
